/**
 * useEmployeeFullProfileSafeMutations (V62)
 *
 * 직원 전체 프로필 안전 수정 훅.
 * 필드 위험도 기반 수정 (LOW/MID/HIGH/BLOCKED).
 */

import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';

// ============================================
// 필드 위험도 분류 (서버와 동일)
// ============================================

export type FieldRisk = 'LOW' | 'MID' | 'HIGH' | 'BLOCKED';

export const FIELD_RISK: Record<string, FieldRisk> = {
  phoneNumber: 'LOW',
  address: 'LOW',
  affiliationName: 'LOW',
  educationLevel: 'LOW',
  major: 'LOW',
  educationYears: 'LOW',
  certificatesText: 'LOW',
  trainingsText: 'LOW',
  commentText: 'LOW',
  priorCareerYears: 'LOW',
  promotionBonusYears: 'LOW',
  currentPositionStartDate: 'LOW',

  nameKo: 'MID',
  gender: 'MID',
  birthDate: 'MID',
  nationalityType: 'MID',

  // V62 안정화: 조직/직위/직책/사번 → 발령/전용 로직에서만 변경
  orgUnitId: 'BLOCKED',
  positionId: 'BLOCKED',
  titleId: 'BLOCKED',
  employeeNumber: 'BLOCKED',
  email: 'BLOCKED',
  residentRegistrationNo: 'BLOCKED',
  firstHireDate: 'BLOCKED',
  ngleFirstHireDate: 'BLOCKED',
  probationEndDate: 'BLOCKED',
  status: 'BLOCKED',
  leaveDate: 'BLOCKED',
  currentEmploymentType: 'BLOCKED',
  currentEmploymentTypeCode: 'BLOCKED',
  roleIds: 'BLOCKED',
  assignedRoleIds: 'BLOCKED',
  contractHireDate: 'BLOCKED',
  contractEndDate: 'BLOCKED',
  dispatchHireDate: 'BLOCKED',
  dispatchEndDate: 'BLOCKED',
  internHireDate: 'BLOCKED',
  internEndDate: 'BLOCKED',
  regularHireDate: 'BLOCKED',
  companyCode: 'BLOCKED',
  userType: 'BLOCKED',
};

// ============================================
// Hook
// ============================================

interface UpdateFullProfileResult {
  success: boolean;
  message: string;
  updatedFields: string[];
}

export function useEmployeeFullProfileSafeMutations() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateFullProfile = useCallback(async (
    employeeId: string,
    changes: Record<string, any>,
    reasons?: Record<string, string>,
    currentUpdatedAt?: string,  // V62 보강: 동시 수정 방지
  ): Promise<boolean> => {
    if (!functions) {
      setError('Firebase Functions가 초기화되지 않았습니다.');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const fn = httpsCallable<
        { employeeId: string; changes: Record<string, any>; reasons?: Record<string, string>; currentUpdatedAt?: string },
        UpdateFullProfileResult
      >(functions, 'updateEmployeeFullProfileSafe');

      const result = await fn({ employeeId, changes, reasons, currentUpdatedAt });
      return result.data.success;
    } catch (err: any) {
      console.error('[updateEmployeeFullProfileSafe] 실패:', err);
      const msg = err.message || '직원 정보 수정에 실패했습니다.';
      setError(msg);
      alert(msg);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { updateFullProfile, isLoading, error };
}

/**
 * 변경된 필드만 추출 (diff)
 */
export function diffChanges(
  original: Record<string, any>,
  edited: Record<string, any>,
): Record<string, any> {
  const changes: Record<string, any> = {};
  for (const key of Object.keys(edited)) {
    const origVal = original[key] ?? '';
    const editVal = edited[key] ?? '';
    if (String(origVal) !== String(editVal)) {
      changes[key] = editVal;
    }
  }
  return changes;
}

/**
 * 변경 필드 중 특정 위험도 필드만 추출
 */
export function filterFieldsByRisk(
  changes: Record<string, any>,
  risk: FieldRisk,
): string[] {
  return Object.keys(changes).filter(key => FIELD_RISK[key] === risk);
}
